import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import FirstLocationRedirectHook from "@/components/FirstLocationRedirectHook.tsx";
import Loading from "@/components/Loading/Index";
import { useDispatchEnabled } from "@/hooks/dispatch/useDispatchEnabled";
import { createLogger } from "@/logger";
import { forceReload } from "@/models/BootstrapUtils.ts";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";

const log = createLogger("bootstrap");

/**
 * Marks — per workspace, for the current tab session — that we already ran the one-shot self-heal
 * for a local DB that resolved an empty channel list. Keying by workspace gives each workspace a
 * single automatic hard-reboot attempt; the marker is cleared the moment a non-empty channel list
 * proves the DB recovered, so a later divergence can heal again. It survives the reboot's reload
 * (hardReboot clears IndexedDB, not sessionStorage), which is what makes "exactly once" hold.
 */
function autoHealKey(workspaceId: string): string {
  return `dbAutoHeal:${workspaceId}`;
}

function hasAttemptedAutoHeal(workspaceId: string): boolean {
  try {
    return sessionStorage.getItem(autoHealKey(workspaceId)) === "1";
  } catch {
    return false;
  }
}

function markAutoHealAttempted(workspaceId: string): void {
  try {
    sessionStorage.setItem(autoHealKey(workspaceId), "1");
  } catch {
    // sessionStorage unavailable — the in-memory ref still prevents a double-trigger this load.
  }
}

function clearAutoHealAttempted(workspaceId: string): void {
  try {
    sessionStorage.removeItem(autoHealKey(workspaceId));
  } catch {
    // ignore
  }
}

export default function FirstLocationRedirect() {
  const { currentWorkspaceId, myCurrentWorkspaceMembership } = React.useContext(WorkspaceContext);
  const { myAccount } = React.useContext(MyAccountContext);
  const [clearingDb, setClearingDb] = useState(false);
  const [initialTimeout, setInitialTimeout] = useState(false);
  const navigate = useNavigate();
  const { redirectUrl, isChannelListLoading, isChannelListEmpty, list } = FirstLocationRedirectHook();
  // A workspace with Dispatch on lands there instead of the first driver's channel: Dispatch stands in for the driver
  // view, and it needs no channel to open on.
  const dispatchEnabled = useDispatchEnabled(currentWorkspaceId ?? undefined);
  const urlToRedirectTo =
    dispatchEnabled && currentWorkspaceId ? `/workspaces/${currentWorkspaceId}/dispatch` : redirectUrl("drivers");
  const autoHealTriggeredRef = useRef(false);

  const listReadyPrereqs = useMemo(
    () => Boolean(currentWorkspaceId && myCurrentWorkspaceMembership?.id && myAccount?.id),
    [currentWorkspaceId, myCurrentWorkspaceMembership?.id, myAccount?.id],
  );

  useEffect(() => {
    if (!listReadyPrereqs) {
      setInitialTimeout(false);
      return;
    }
    const t = window.setTimeout(() => setInitialTimeout(true), 3000);
    return () => window.clearTimeout(t);
  }, [listReadyPrereqs]);

  useEffect(() => {
    if (urlToRedirectTo) {
      navigate(urlToRedirectTo);
    }
  }, [urlToRedirectTo, navigate]);

  // A resolved non-empty list proves the local DB is healthy: drop the self-heal marker so a future
  // divergence in this workspace gets its own fresh one-shot attempt.
  useEffect(() => {
    if (currentWorkspaceId && list != null && list.length > 0) {
      clearAutoHealAttempted(currentWorkspaceId);
    }
  }, [currentWorkspaceId, list]);

  /**
   * The local DB resolved an empty channel list even though the workspace, membership, and account
   * all loaded and the settle window elapsed. This means the bootstrap watermark diverged from the
   * local data (rows missing though the workspace is marked bootstrapped), so the app will never
   * self-correct on its own.
   */
  const needsRecovery = listReadyPrereqs && initialTimeout && isChannelListEmpty;
  const alreadyAttemptedAutoHeal = Boolean(currentWorkspaceId) && hasAttemptedAutoHeal(currentWorkspaceId);

  // First hit: hard-reboot once (clears the local cache + registry and forces a clean bootstrap).
  // If the empty list recurs after that single attempt, fall through to the manual control below
  // rather than reboot-looping.
  useEffect(() => {
    if (!needsRecovery || !currentWorkspaceId || alreadyAttemptedAutoHeal || autoHealTriggeredRef.current) {
      return;
    }
    autoHealTriggeredRef.current = true;
    markAutoHealAttempted(currentWorkspaceId);
    log.warn(
      "[FirstLocationRedirect] Empty channel list with a completed bootstrap; performing one-shot local DB self-heal",
      { workspaceId: currentWorkspaceId },
    );
    setClearingDb(true);
    // A rejected reboot must clear the in-flight flag; otherwise the UI is stranded on "Clearing…"
    // with no reload. The marker is already set, so the manual control takes over on the next load.
    void forceReload().catch((error: unknown) => {
      log.error("[FirstLocationRedirect] One-shot self-heal reload failed", {
        error,
        workspaceId: currentWorkspaceId,
      });
      setClearingDb(false);
    });
  }, [needsRecovery, currentWorkspaceId, alreadyAttemptedAutoHeal]);

  const onClearDatabase = () => {
    setClearingDb(true);
    void forceReload().catch((error: unknown) => {
      log.error("[FirstLocationRedirect] Manual DB clear reload failed", { error });
      setClearingDb(false);
    });
  };

  // Surface the manual control only after the automatic attempt has been spent and isn't in flight.
  const showManualDbError = needsRecovery && alreadyAttemptedAutoHeal && !clearingDb;

  /** While [] may be transient (bootstrap still filling the local DB), keep the loading UI until this elapses. */
  const showLoading =
    !listReadyPrereqs ||
    isChannelListLoading ||
    (isChannelListEmpty && !initialTimeout) ||
    (needsRecovery && !showManualDbError);

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 10,
        display: "flex",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box>
        {showLoading && (
          <Box sx={{ display: "flex" }}>
            <Box sx={{ mr: 2 }}>Loading </Box>
            <Loading variant="white" size="large" baseSize={6} />
          </Box>
        )}

        {showManualDbError && (
          <Box sx={{ maxWidth: 420, textAlign: "center" }}>
            <Typography sx={{ mb: 2 }}>
              Local data could not load a channel list. Your local database may be out of sync. Click below to clear the
              local data, then the app will reload.
            </Typography>
            <Button disabled={clearingDb} variant="contained" color="primary" onClick={onClearDatabase}>
              {clearingDb ? "Clearing…" : "Clear local database and reload"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
